import { gql } from "@apollo/client";
import { useCallback } from "react";
import { useMutation } from "@apollo/client/react";

import type {
  UserDeleteResp,
  UserDeleteVariables,
  User
} from "../../types/userTypes";

const DELETE_USER = gql`
  mutation DeleteUser($attributes: DeleteUserInput!) {
    deleteUser(attributes: $attributes) {
      user {
        id
        email
      }
      errors
    }
  }
`   

type UseDeleteUserOptions = {
  onCompleted?: (deactivatedUser: User) => void;
  onError?: (error: unknown) => void;
  mutationOptions?: Parameters<typeof useMutation<UserDeleteResp, UserDeleteVariables>>[1];
};

const useDeleteUser = (options?: UseDeleteUserOptions) => {
  const [mutate, { data, loading, error }] = useMutation<
    UserDeleteResp,
    UserDeleteVariables
  >(DELETE_USER, options?.mutationOptions);

  const deleteUser = useCallback(
    async (input: { id: number, password: string }) => {
      try {
        const result = await mutate({
          variables: {
            attributes: {
              id: input.id!,
              password: input.password
            },
          },
        });

        const deleteUser = result?.data?.deleteUser;

        if (deleteUser?.errors?.length) {
          throw new Error(deleteUser.errors.join(", "));
        }

        if (options?.onCompleted && deleteUser?.user) {
          options.onCompleted(deleteUser.user);
        }

        return deleteUser?.user;
      } catch (err) {
        if (options?.onError) {
          options.onError(err);
        }
        throw err;
      }
    },
    [mutate, options]
  );

  const user = data?.deleteUser?.user;

  return {
    deleteUser,
    deletedUser: user,
    loading,
    error: error as Error | null | undefined,
  };
}

export default useDeleteUser
